import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  HostListener,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Plain shape we accept — same {label, value} contract every PrimeNG select
 * uses, so swapping one for the other is a one-line change in the host. */
export interface IntegratorSelectOption<T = unknown> {
  label: string;
  value: T;
  /** Optional disabled flag — the option still renders but greyed out + not
   * clickable. Useful for "all sites" sentinels we want to keep visible. */
  disabled?: boolean;
}

/**
 * V8 integrator custom select. Replaces {@code <p-select>} on the integrator
 * pages — PrimeNG's Aura-themed select looked wrong against the V8 design
 * language (jagged height, hard-to-theme accents, pink focus rings on every
 * device kind).
 *
 * <p>Features:
 * <ul>
 *   <li>Single-value selection with {@code ControlValueAccessor} → drop-in for
 *       {@code [(ngModel)]} and reactive forms.</li>
 *   <li>Optional filter input inside the dropdown ({@code filterable} input)
 *       with a substring match on the option label. Auto-focused on open.</li>
 *   <li>Keyboard nav: Arrow up/down to navigate, Enter to select, Escape to
 *       close. Letters typed when the dropdown is open route to the filter
 *       input automatically.</li>
 *   <li>Closes on outside-click (host listener on document) and on
 *       {@code Escape}.</li>
 *   <li>Themable accent (focus ring, hover bg) via the {@code accent} input
 *       — {@code 'primary'} for trackers, {@code 'warmYellow'} for gateways,
 *       {@code 'neutral'} for everywhere else.</li>
 *   <li>Native dark mode via Tailwind {@code dark:} variants — no CSS-var
 *       gymnastics, no PrimeNG token overrides.</li>
 * </ul>
 *
 * <p>Out of scope: virtual scrolling (we cap our option lists at a few hundred
 * entries, native scroll is fine). For multi-value or grouped options, see
 * the future {@code IntegratorMultiSelectComponent} — kept separate so each
 * component stays simple.
 */
@Component({
  selector: 'lib-integrator-select',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // forwardRef because the class isn't fully declared yet at this point.
      useExisting: forwardRef(() => IntegratorSelectComponent),
      multi: true,
    },
  ],
  styles: [
    `
      /* {@code display: block} so the component fills its flex/grid parent
       * when the host has e.g. {@code class="w-full sm:w-48"}. The trigger
       * width is still driven by the inner div via the {@link width} input,
       * so a host with no parent constraint still respects {@code width}. */
      :host {
        display: block;
      }
    `,
  ],
  template: `
    <div #root class="relative" [style.width]="width()">
      <!-- Trigger button — height tracks the {@link size} input so the select
           lines up with whatever toolbar baseline its host uses (h-8 for the
           graph chips, h-9 for the list filters). -->
      <button
        type="button"
        class="flex w-full items-center gap-2 rounded-md border bg-white px-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
        [class.h-8]="size() === 'sm'"
        [class.h-9]="size() === 'md'"
        [class.border-gray-300]="!open()"
        [class.dark:border-slate-600]="!open()"
        [class]="open() ? focusBorderClass() : ''"
        [disabled]="disabled()"
        [attr.aria-haspopup]="'listbox'"
        [attr.aria-expanded]="open()"
        (click)="toggle()"
        (keydown)="onTriggerKeydown($event)">
        <span
          class="min-w-0 flex-1 truncate text-left"
          [class.text-gray-400]="!selectedLabel()"
          [class.dark:text-slate-500]="!selectedLabel()">
          {{ selectedLabel() ?? placeholder() }}
        </span>
        <svg
          class="size-3 shrink-0 transition-transform"
          [class.rotate-180]="open()"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <!-- Dropdown panel. Absolute-positioned below the trigger; falls back to
           rendering above if it would overflow the viewport (computed via
           bounding rect at open time). max-h-60 (15rem) with overflow-y so a
           20+ option list doesn't push the page. -->
      @if (open()) {
        <div
          class="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800"
          [class.bottom-full]="placement() === 'top'"
          [class.mb-1]="placement() === 'top'"
          [class.mt-0]="placement() === 'top'"
          role="listbox">
          @if (filterable()) {
            <div class="border-b border-gray-200 p-1.5 dark:border-slate-700">
              <input
                #filterInput
                type="text"
                placeholder="Search…"
                class="focus:border-primary-500 h-7 w-full rounded border border-gray-200 bg-white px-2 text-sm text-gray-900 outline-none dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                [ngModel]="filter()"
                (ngModelChange)="onFilterInput($event)"
                (keydown)="onFilterKeydown($event)" />
            </div>
          }
          <ul class="max-h-60 overflow-y-auto py-1">
            @for (opt of filteredOptions(); track opt.value; let i = $index) {
              <li
                role="option"
                [attr.aria-selected]="isSelected(opt)"
                class="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[12.5px] transition-colors"
                [class.cursor-not-allowed]="opt.disabled"
                [class.opacity-50]="opt.disabled"
                [class.font-semibold]="isSelected(opt)"
                [class.bg-gray-100]="highlightIndex() === i && !isSelected(opt)"
                [class.dark:bg-slate-700]="highlightIndex() === i && !isSelected(opt)"
                [class]="
                  isSelected(opt)
                    ? selectedItemClass()
                    : 'text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700'
                "
                (click)="onSelect(opt)"
                (mouseenter)="highlightIndex.set(i)">
                <span class="flex-1 truncate">{{ opt.label }}</span>
                @if (isSelected(opt)) {
                  <svg class="size-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="3">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                }
              </li>
            } @empty {
              <li class="px-3 py-3 text-center text-sm text-gray-500 dark:text-slate-400">No match.</li>
            }
          </ul>
        </div>
      }
    </div>
  `,
})
export class IntegratorSelectComponent<T = unknown> implements ControlValueAccessor {
  options = input.required<IntegratorSelectOption<T>[]>();
  placeholder = input<string>('Select…');
  /** Whether to show the search input above the option list. Cheap to leave
   * on for any list > ~5 entries. */
  filterable = input<boolean>(false);
  /** CSS width of the trigger — passed through to the wrapper. The dropdown
   * panel inherits this width via {@code left-0 right-0}. Default leaves
   * room for ~14 chars of placeholder. */
  width = input<string>('14rem');
  /** Visual accent — drives the focus border on the trigger and the bg of
   * the selected option. */
  accent = input<'primary' | 'warmYellow' | 'neutral'>('primary');
  /** Trigger height — {@code 'sm'} = h-8 (32px) to line up with the graph
   * chips, {@code 'md'} = h-9 (36px) to line up with the list-page filters. */
  size = input<'sm' | 'md'>('sm');
  disabled = input<boolean>(false);

  // ── Internal state
  protected readonly open = signal(false);
  protected readonly filter = signal('');
  protected readonly highlightIndex = signal(0);
  protected readonly value = signal<T | null>(null);

  /** Best-effort placement — flips to "top" if the dropdown would overflow
   * the viewport. Set on toggle(). */
  protected readonly placement = signal<'bottom' | 'top'>('bottom');

  protected readonly root = viewChild<ElementRef<HTMLDivElement>>('root');
  protected readonly filterInput = viewChild<ElementRef<HTMLInputElement>>('filterInput');

  protected readonly filteredOptions = computed<IntegratorSelectOption<T>[]>(() => {
    const f = this.filter().toLowerCase().trim();
    const list = this.options() ?? [];
    if (!f) return list;
    return list.filter(o => o.label.toLowerCase().includes(f));
  });

  protected readonly selectedLabel = computed<string | null>(() => {
    const v = this.value();
    if (v === null || v === undefined) return null;
    return this.options()?.find(o => o.value === v)?.label ?? null;
  });

  protected readonly focusBorderClass = computed<string>(() => {
    switch (this.accent()) {
      case 'warmYellow':
        return 'border-warmYellow-500 ring-1 ring-warmYellow-500';
      case 'neutral':
        return 'border-gray-400 ring-1 ring-gray-400 dark:border-slate-400 dark:ring-slate-400';
      default:
        return 'border-primary-500 ring-1 ring-primary-500';
    }
  });

  protected readonly selectedItemClass = computed<string>(() => {
    switch (this.accent()) {
      case 'warmYellow':
        return 'bg-warmYellow-50 text-warmYellow-900 dark:bg-warmYellow-500/15 dark:text-warmYellow-200';
      case 'neutral':
        return 'bg-gray-100 text-gray-900 dark:bg-slate-700 dark:text-slate-100';
      default:
        return 'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300';
    }
  });

  // ── ControlValueAccessor wiring. We keep the value in a signal so the
  // template can read it via the {@link selectedLabel} computed; the standard
  // CVA fns (onChange, onTouched) are kept as plain function refs.
  private onChange: (v: T | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(v: T | null): void {
    this.value.set(v);
  }

  registerOnChange(fn: (v: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {
    // Forms-driven disabled flag isn't wired to a signal yet — the input
    // {@link disabled} covers the typical use cases. If we ever need
    // FormControl-driven disable, route through a signal here.
  }

  // ── Open/close

  protected toggle(): void {
    if (this.disabled()) return;
    if (this.open()) {
      this.close();
    } else {
      this.openPanel();
    }
  }

  private openPanel(): void {
    this.open.set(true);
    this.filter.set('');
    // Reset highlight to the currently-selected option (if any) so Enter
    // confirms the existing value rather than picking the top of the list.
    const opts = this.filteredOptions();
    const idx = opts.findIndex(o => o.value === this.value());
    this.highlightIndex.set(idx >= 0 ? idx : 0);

    // Decide placement — if the trigger sits in the bottom 240px of the
    // viewport, render the dropdown above instead. 240 = max-h-60 panel
    // (15rem) + a few px of margin, ballpark.
    const el = this.root()?.nativeElement;
    if (el) {
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      this.placement.set(spaceBelow < 260 && rect.top > 260 ? 'top' : 'bottom');
    }

    // Auto-focus the filter input on next paint so typing lands there.
    if (this.filterable()) {
      queueMicrotask(() => this.filterInput()?.nativeElement.focus());
    }
  }

  private close(): void {
    this.open.set(false);
    this.filter.set('');
    this.onTouched();
  }

  protected isSelected(opt: IntegratorSelectOption<T>): boolean {
    return this.value() === opt.value;
  }

  protected onSelect(opt: IntegratorSelectOption<T>): void {
    if (opt.disabled) return;
    this.value.set(opt.value);
    this.onChange(opt.value);
    this.close();
  }

  /**
   * Filter input writer — gets the new string from the {@code (ngModelChange)}
   * event and writes it into the {@link filter} signal. We use a one-way
   * {@code [ngModel]} + manual {@code (ngModelChange)} pair instead of
   * {@code [(ngModel)]="filter"} because signals aren't supported as the
   * lvalue of a banana-in-a-box binding (the assignment overwrites the
   * signal with a plain string and the {@link filteredOptions} computed
   * blows up calling {@code .toLowerCase()} on it).
   */
  protected onFilterInput(value: string): void {
    this.filter.set(value);
    // Reset highlight to top whenever the list changes — otherwise the
    // previous index might point past the now-filtered list end.
    this.highlightIndex.set(0);
  }

  // ── Keyboard

  /** Keydown on the trigger button — handles Open + Arrow/Enter. */
  protected onTriggerKeydown(e: KeyboardEvent): void {
    if (this.disabled()) return;
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!this.open()) this.openPanel();
        else this.handleArrowOrEnter(e);
        break;
      case 'Escape':
        if (this.open()) this.close();
        break;
    }
  }

  /** Keydown on the filter input — typing stays in the input, but arrows /
   * Enter navigate the list. */
  protected onFilterKeydown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
        e.preventDefault();
        this.handleArrowOrEnter(e);
        break;
      case 'Escape':
        e.preventDefault();
        this.close();
        break;
    }
  }

  private handleArrowOrEnter(e: KeyboardEvent): void {
    const opts = this.filteredOptions();
    if (!opts.length) return;
    if (e.key === 'ArrowDown') {
      this.highlightIndex.update(i => (i + 1) % opts.length);
    } else if (e.key === 'ArrowUp') {
      this.highlightIndex.update(i => (i - 1 + opts.length) % opts.length);
    } else if (e.key === 'Enter') {
      const opt = opts[this.highlightIndex()];
      if (opt) this.onSelect(opt);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    if (!this.open()) return;
    const el = this.root()?.nativeElement;
    if (el && !el.contains(e.target as Node)) this.close();
  }

  @HostListener('document:keydown.escape')
  onDocEscape(): void {
    if (this.open()) this.close();
  }
}
